import {
    AfterViewInit,
    Component,
    ComponentRef,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    Output,
    ViewChild,
    ViewContainerRef,
} from "@angular/core";
import { PCP_LOGGER } from "@pcp/logging";
import { ReplaySubject, Subscription } from "rxjs";
import { take } from "rxjs/operators";
import { RubberElementInput } from "../interfaces";
import { CanvasInteractionEvent } from "@pcp/core/interfaces";
import { RubberElementContentComponent } from "../rubber-element-content/rubber-element-content.component";

/**
 * Dynamically generated iframe
 * including the Pcp RubberElementContentComponent.
 */
@Component({
    selector: 'pcp-rubber-element-iframe',
    templateUrl: './iframe.component.html',
    styleUrls: ['./iframe.component.scss']
})
export class RubberElementIframeComponent implements AfterViewInit, OnDestroy {
    private readonly log = inject(PCP_LOGGER).createScoped('slide-sync');

    @ViewChild('iframe') iframe!: ElementRef<HTMLIFrameElement>;

    @Output() interactions = new EventEmitter<CanvasInteractionEvent>();
    @Output() componentCreated = new EventEmitter<RubberElementContentComponent>();

    private doc: null | Document = null;
    private componentRef?: ComponentRef<RubberElementContentComponent>;
    private componentOutputSubscription?: Subscription;
    private afterViewInit$ = new ReplaySubject<void>();

    private readonly viewContainerRef = inject(ViewContainerRef);

    @Input() set rubberImage(image: RubberElementInput) {
        this.afterViewInit$.pipe(take(1)).subscribe(() => {
            this.ngOnDestroy(); // remove + destory old component ref if any
            this.createComponent(image);
        });
    }

    @Input() disableElementUsage: boolean;

    createComponent(image: RubberElementInput) {
        this.componentRef = this.viewContainerRef.createComponent(RubberElementContentComponent, {});
        this.componentRef.instance.rubberImage = image;
        this.componentRef.changeDetectorRef.detectChanges();

        if (!this.doc) {
            this.log.error('cannot create iframe, document does not exist');
            return;
        }

        this.doc.body.style.margin = "0";
        this.doc.body.appendChild(this.componentRef.location.nativeElement);

        this.componentOutputSubscription?.unsubscribe();
        this.componentOutputSubscription = this.componentRef.instance.interactions.subscribe(e => this.interactions.next(e));
        this.componentCreated.next(this.componentRef.instance);
    }

    ngAfterViewInit() {
        this.doc = this.iframe.nativeElement.contentDocument;
        this.afterViewInit$.next();
    }

    ngOnDestroy() {
        if (this.componentRef) {
            this.componentRef.destroy();
        }
        this.componentOutputSubscription?.unsubscribe();
    }
}
